import { createSelector } from 'reselect';
import { convertirAMoneda, convertirANumero } from '../../utils/currency';

// Selectores
export const selectPerfiladorInputValue = (state) => state.perfilador.inputValue;
export const selectPerfiladorInputNumber = (state) => state.perfilador.inputNumber;
export const selectPerfiladorResultado = (state) => state.perfilador.resultado;
export const selectPerfiladorPlazo = (state) => state.perfilador.plazo;
export const selectPerfiladorPlazoNumber = (state) => state.perfilador.plazoNumber;
export const selectPerfiladorEnganche = (state) => state.perfilador.engancheValue;
export const selectPerfiladorEngancheRecursosPropios = (state) => (
  state.perfilador.engancheRecursosPropiosValue
);
export const selectPerfiladorEngancheRecursosPropiosNumber = (state) => (
  state.perfilador.engancheRecursosPropiosNumber
);
// Selectores base: devuelven números directamente
export const selectPerfiladorSaldoSubcuentaInfonavit = (state) => (
  state.perfilador.saldoSubcuentaInfonavit || 0
);
export const selectPerfiladorMontoCreditoInfonavit = (
  state,
) => state.perfilador.montoCreditoInfonavit || 0;

export const selectMontoTotalInfonavit = createSelector(
  [selectPerfiladorSaldoSubcuentaInfonavit, selectPerfiladorMontoCreditoInfonavit],
  (saldoSubcuentaInfonavit, montoCreditoInfonavit) => {
    const saldoSubcuentaInfonavitNumber = convertirANumero(saldoSubcuentaInfonavit) || 0;
    const montoCreditoInfonavitNumber = convertirANumero(montoCreditoInfonavit) || 0;
    return saldoSubcuentaInfonavitNumber + montoCreditoInfonavitNumber;
  },
);

// Selectores memoizados para valores formateados
export const selectPerfiladorMontoCreditoInfonavitFormatted = createSelector(
  [selectPerfiladorMontoCreditoInfonavit],
  (montoCreditoInfonavit) => convertirAMoneda(montoCreditoInfonavit || 0),
);
export const selectPerfiladorSaldoSubcuentaInfonavitFormatted = createSelector(
  [selectPerfiladorSaldoSubcuentaInfonavit],
  (saldoSubcuentaInfonavit) => convertirAMoneda(saldoSubcuentaInfonavit || 0),
);

export const selectPerfiladorMontoCredito = (state) => state
  .perfilador?.resultado?.montoCredito || 0;
export const selectPerfiladorIsMontoCreditoManual = (state) => state
  .perfilador?.isMontoCreditoManual || false;
// Porcentaje del enganche total (enganche general)
export const selectPerfiladorPercentageEnganche = (state) => state.perfilador.percentageEnganche;
// Porcentaje de recursos propios (usado por los botones +/-)
export const selectPerfiladorPercentageEngancheRecursosPropios = (state) => (
  state.perfilador.percentageEngancheRecursosPropios
);
export const selectPerfiladorEngancheNumber = (state) => state.perfilador.engancheNumber;
export const selectPerfiladorInitialDataValues = (state) => state.perfilador.initialDataValues;
export const selectPerfiladorTasaNumber = (state) => state.perfilador.tasaNumber;
export const selectPerfiladorengancheMinimoPercentage = (
  state,
) => state.perfilador.engancheMinimoPercentage;

// export const selectPerfiladorEngancheMinimoPercentageRecursosPropios = (
//   state,
// ) => state.perfilador.engancheMinimoPercentageRecursosPropios

export const selectPerfiladorEngancheMinimoRecursosPropiosPercentage = createSelector(
  [
    selectPerfiladorInputNumber,
    selectPerfiladorengancheMinimoPercentage,
    selectPerfiladorMontoCreditoInfonavit,
    selectPerfiladorSaldoSubcuentaInfonavit,
  ],
  (inputNumber, engancheMinimoPercentage, montoCreditoInfonavit, saldoSubcuentaInfonavit) => {
    const engancheMinimoRequerido = (inputNumber * engancheMinimoPercentage) / 100;
    const montoTotalInfonavit = montoCreditoInfonavit + saldoSubcuentaInfonavit;
    if (montoTotalInfonavit >= engancheMinimoRequerido) {
      return 0;
    }
    const recursosFaltantes = engancheMinimoRequerido - montoTotalInfonavit;
    return (recursosFaltantes / inputNumber) * 100;
  },
);

export const selectPerfiladorGastosEscrituraPercentage = (
  state,
) => state.perfilador.gastosEscrituraPercentage;

export const selectPerfiladorEngancheRangos = (state) => state.perfilador.engancheRangos;

/**
 * Selector derivado para calcular los rangos de monto de crédito
 * basándose en el valor de la propiedad y los rangos de enganche
 * montoCreditoMinimo = precioInmueble - engancheMaximo
 * montoCreditoMaximo = precioInmueble - engancheMinimo
 */
export const selectPerfiladorMontoCreditoRangos = createSelector(
  [
    selectPerfiladorInputNumber,
    selectPerfiladorEngancheRangos,
    selectMontoTotalInfonavit,
  ],
  (inputNumber, engancheRangos, montoTotalInfonavit) => {
    if (!inputNumber || inputNumber <= 0) {
      return { minimo: 0, maximo: 0 };
    }

    const engancheMinimoTotal = engancheRangos.minimo + montoTotalInfonavit;
    const engancheMaximoTotal = engancheRangos.maximo + montoTotalInfonavit;

    const montoCreditoMaximo = Math.max(0, inputNumber - engancheMinimoTotal);
    const montoCreditoMinimo = Math.max(0, inputNumber - engancheMaximoTotal);

    return {
      minimo: montoCreditoMinimo,
      maximo: montoCreditoMaximo,
    };
  },
);

export const selectPerfiladorInmobiliariaConditions = (
  state,
) => state.perfilador.inmobiliariasConditions;

export const selectPerfiladorCurrentInmobiliariaId = (
  state,
) => state.perfilador.currentInmobiliariaId;

export const selectPICByCurrentInmobiliariaId = createSelector(
  [selectPerfiladorInmobiliariaConditions],
  (inmobiliariasConditions) => inmobiliariasConditions[selectPerfiladorCurrentInmobiliariaId],
);
// Selector memoizado
export const selectPerfiladorFormattedResultado = createSelector(
  [selectPerfiladorResultado],
  (resultado) => ({
    ...resultado,
    formattedIngresosRecomendados: new Intl.NumberFormat('es-MX').format(resultado.ingresosRecomendados),
  }),
);
